// One settings document's page, `configuration/<workload>/<document>`
// (02-DESIGN/06-configuration/01-the-configuration-service.md § the console:
// Configuration; 01-the-console.md § the address; § conventions: a thing's
// detail is behind its row, a name is a link; hq decision 0069): the document
// as text with its version beside it, saved with the version as the guard and
// a conflict shown in the person's words, deleted from the page's menu with a
// confirmation; the crumb goes back to where it was opened from —
// Configuration, or the workload's Settings tab, named in the link's state —
// and so does a delete. A Configuration row and a workload's Settings row
// both open it. A document not saved under the name, and a workload the
// service does not answer, are said, never an editor: adding is
// Configuration's *Add settings*. The workload is named as a link to
// its page where the role is offered Workloads. In a managed workspace the
// required document is the steward's, read here and never written.

import { identity } from "@impire-io/sdk";
import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { usePage } from "../../platform/session.tsx";
import { pageOffered } from "../../roles/offers.ts";
import { Empty, NotAllowed, Notice, PageHead, Pending, useLoad } from "../../ui/components.tsx";
import { withForm } from "../../ui/form.tsx";
import { NameLink } from "../../ui/ident.tsx";
import { RowMenu } from "../../ui/table.tsx";
import { Code, DOCUMENT_REQUIRED, DOCUMENT_SETTINGS, validDocumentName } from "../../wire/config.ts";
import {
  idSubject,
  RegistrationState,
  SERVICE,
  validWorkloadID,
  type Workload,
} from "../../wire/workloads.ts";
import { useSurface } from "../surface.tsx";
import { useManaged } from "../workspaces/managed.ts";
import { backOf, documentReturn, documentView } from "./documents.ts";
import { DocumentEditor, settingsMenu } from "./WorkloadSettings.tsx";

export function DocumentPage() {
  const { workload = "", document = "" } = useParams();
  const s = useSurface();
  if (!validWorkloadID(workload) || !validDocumentName(document)) {
    return (
      <PageHead
        crumb={<Link to={tenantPath(s.tenant, "configuration")}>Configuration</Link>}
        title="Not settings"
        line={`"${workload}/${document}" is not a workload's settings.`}
      />
    );
  }
  return <One key={`${workload}/${document}`} workload={workload} document={document} />;
}

function One({ workload, document }: { workload: string; document: string }) {
  const s = useSurface();
  const { platform } = usePage();
  const navigate = useNavigate();
  const location = useLocation();
  const managed = useManaged();
  const [tick, setTick] = useState(0);
  const [owner] = useLoad(`configuration/${workload}/workload`, () =>
    s.bus.call<Workload>(SERVICE, idSubject("get", workload), undefined),
  );
  const [read] = useLoad(
    `configuration/${workload}/${document}`,
    () => s.bus.configuration().get(workload, document),
    tick,
  );
  const [note, setNote] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<unknown>(undefined);

  const w = owner.state === "ready" ? owner.value : undefined;
  const name = w !== undefined && w.name !== "" ? w.name : workload;
  // Where the page was opened from: the crumb's target, and where a delete returns.
  const back = documentReturn(s.tenant, location.state);
  const fromConfiguration = backOf(location.state) === undefined;
  const crumb = (
    <>
      <Link to={back.to} state={back.state}>
        {back.label}
      </Link>
      {fromConfiguration && (
        <>
          <span>/</span>
          <span>{name}</span>
        </>
      )}
      <span>/</span>
      <span className="mono">{document}</span>
    </>
  );
  // The workload, named as a link to its page where the role is offered it (§ conventions: a name is a link).
  const named = pageOffered(s.role, "workloads") ? (
    <NameLink to={tenantPath(s.tenant, "workloads", workload)}>{name}</NameLink>
  ) : (
    name
  );
  // In a managed workspace the required document is the steward's, written
  // from the blueprint: read here, never written (02-DESIGN/13-blueprints/03-required.md § the console).
  const locked = document === DOCUMENT_REQUIRED && managed.managed;
  const retired = w?.state === RegistrationState.retired;
  // A workload the service does not answer leaves nothing to edit (documentView).
  const canEdit = w !== undefined && s.offered("configuration:any") && !locked && !retired;
  const model = platform.images?.[identity.Image.inference] || undefined;
  const hint = document === DOCUMENT_SETTINGS && w !== undefined && model !== undefined && w.image === model;
  const entry = read.state === "ready" ? read.value : undefined;
  // Nothing saved under the name is said, not a failure and not an editor.
  const missing = read.state === "refused" && isRefusal(read.err, Code.notFound);
  const view = documentView(owner.state, missing ? "missing" : read.state);

  const remove = () => {
    setErr(undefined);
    s.bus
      .configuration()
      .delete(workload, document)
      .then(
        () => {
          void navigate(back.to, { state: back.state });
        },
        (e: unknown) => {
          setErr(e);
        },
      );
  };

  return (
    <>
      <PageHead
        crumb={crumb}
        title={document}
        line={
          <>
            {locked ? `Required by ${managed.by}` : "Settings"} of {named}
            {entry !== undefined && <> · version {entry.revision}</>}
          </>
        }
        action={
          canEdit && entry !== undefined ? (
            <RowMenu label={`Actions for ${document}`} entries={settingsMenu(document, remove)} />
          ) : undefined
        }
      />
      {err !== undefined && <NotAllowed err={err} />}
      {note !== undefined && (
        <Notice kind="ok">
          {note}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              setNote(undefined);
            }}
          >
            Dismiss
          </button>
        </Notice>
      )}
      {view === "pending" && <Pending what="Reading the settings" />}
      {view === "refused" && owner.state === "refused" && <NotAllowed err={owner.err} what="This workload" />}
      {view === "refused" && read.state === "refused" && !missing && (
        <NotAllowed err={read.err} what="These settings" />
      )}
      {view === "missing" && (
        <Empty>
          Nothing is saved as {document} for {name}.{" "}
          {s.offered("configuration:any") && (
            <Link
              className="text-link"
              to={tenantPath(s.tenant, "configuration")}
              state={withForm(null, "add")}
            >
              Add settings
            </Link>
          )}
        </Empty>
      )}
      {view === "editor" && (
        <DocumentEditor
          key={`${tick}/${String(entry?.revision ?? 0)}`}
          workload={workload}
          document={document}
          entry={entry}
          canEdit={canEdit}
          hint={hint}
          onSaved={(e) => {
            setNote(`Saved as version ${e.revision}.`);
            setTick((t) => t + 1);
          }}
          onReload={() => {
            setTick((t) => t + 1);
          }}
        />
      )}
    </>
  );
}
